import { useEffect, useState } from 'react';
import { Hint, Screen, SecondaryButton, Title } from './kit';

interface Props {
  /** Может быть пустым: родитель мог прийти сюда с другого устройства,
   *  где регистрации не было. Тогда экран спрашивает адрес сам. */
  email: string;
  onVerify: (code: string, email: string) => void;
  onResend: () => void;
  onBack: () => void;
  /** Секунды до того, как повторная отправка снова разрешена. Пауза стоит
   *  на сервере (не чаще письма в минуту одному адресу), и кнопка обязана
   *  показывать её, а не молча отдавать отказ. */
  cooldown: number;
  busy?: boolean;
  error?: string | null;
}

const FIELD =
  'min-h-12 w-full rounded-xl border-2 border-line bg-card p-3 text-[1.05rem] text-ink';

/** Подтверждение почты кодом, а не ссылкой.
 *
 *  Три причины, и третья сильнее двух первых. Отправитель переписывает любой
 *  `href` на свой редиректор — письмо от `mathpaw.com`, ведущее на
 *  незнакомый домен, выглядит как фишинг ровно там, где человек впервые
 *  решает, доверять ли приложению. Почтовый сканер, разворачивая редирект,
 *  может сжечь одноразовый токен. И главное: родитель заводит запись на
 *  планшете ребёнка, а почту читает на своём телефоне — ссылка подтвердит
 *  адрес, но сессию отдаст не тому устройству. Код перепечатывается туда,
 *  куда нужно.
 *
 *  Экран отдельный от восстановления пароля, хотя оба просят код: там после
 *  кода придумывают новый пароль, здесь — нет, и общая форма на два случая
 *  завела бы поле, которое в одном из них лишнее. */
export function ConfirmScreen({
  email, onVerify, onResend, onBack, cooldown, busy, error,
}: Props) {
  const [code, setCode] = useState('');
  const [address, setAddress] = useState(email);

  /** Поле получает фокус само: родитель пришёл сюда из почты с восемью
   *  цифрами в голове. */
  useEffect(() => {
    document.getElementById('confirm-code')?.focus();
  }, []);

  return (
    <Screen>
      <Title>Подтвердите почту</Title>
      <Hint>
        Введите код из письма — и можно заводить профиль ребёнка.
      </Hint>
      <Hint>Ссылок в письме нет, искать их не нужно.</Hint>

      <form
        className="sentry-mask flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim() && address.trim() && !busy) onVerify(code.trim(), address.trim());
        }}
      >
        {/* Адрес виден ВСЕГДА и правится.
            Код проверяется парой «адрес и код», и если адрес не тот —
            сервер отвечает «код неверен», а родитель ищет ошибку в цифрах.
            Подставиться сюда чужой адрес может легко: менеджер паролей
            заполнил поле на экране входа, а мы взяли его оттуда. Пока
            адрес был показан текстом, поправить его было нечем. */}
        <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
          Почта, на которую пришёл код
          <input
            type="email"
            value={address}
            autoComplete="username"
            inputMode="email"
            required
            className={FIELD}
            onChange={(event) => setAddress(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
          Код из письма
          <input
            id="confirm-code"
            type="text"
            value={code}
            /** iOS предлагает подстановку по строке «@mathpaw.com #код»,
             *  которая стоит в самом письме. */
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            /** Не восемь, а с запасом. Длина кода задаётся настройкой
             *  сервера (`otp_length`), и жёсткая восьмёрка здесь означала
             *  бы, что смена настройки молча обрезает код на вводе:
             *  родитель видит в письме верные цифры, вводит их, а поле
             *  отбрасывает последние. Сервер сам скажет, если код не тот. */
            maxLength={12}
            required
            className={FIELD}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
          />
        </label>
        {/* Отказ относится к форме целиком — код истёк, нет связи, предел
            писем, — а полей два, почта и код. Поэтому немедленное объявление
            без связи с полем; абзац появляется только вместе с текстом. */}
        {error && <p role="alert" className="m-0 text-[0.95rem] text-incorrect">{error}</p>}
        <button
          type="submit"
          disabled={code.trim() === '' || address.trim() === '' || busy}
          className="rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#c7cddb]"
        >
          Подтвердить
        </button>
      </form>

      <button
        type="button"
        disabled={cooldown > 0 || busy}
        onClick={onResend}
        className="min-h-11 rounded-lg text-[0.95rem] text-muted underline disabled:no-underline disabled:opacity-60"
      >
        {cooldown > 0 ? `Прислать новый код можно через ${cooldown} с` : 'Прислать новый код'}
      </button>

      <SecondaryButton bottom onClick={onBack}>
        Назад ко входу
      </SecondaryButton>
    </Screen>
  );
}
